import React, { useCallback } from 'react'

import { ProductV2, formatPriceText } from '@common'
import { useTranslations } from 'next-intl'
import Image from 'next/legacy/image'

import { BuyButton } from '@/features/products/BuyButton'
import { resolveProductImage } from '@/utils/resolveProductImage'

import { scrollModal } from '../../components/Modal'
import { Ribbon } from '../../components/Ribbon'
import { useModalContext } from '../../contexts/ModalContext'
import { useTranslatable } from '../../localization/useTranslatable'
import { useAppSelector } from '../../utils/useAppSelector'
import { resolveRibbonKey } from '../common/useProducts'
import { useLocale } from '../locale/useLocale'
import ProductDescription from './ProductDescription'

import styles from './HeroProduct.module.scss'

type Props = {
  product: ProductV2
}

function getRibbonContent(product: ProductV2) {
  if (product.offerData?.isBestDeal) {
    return ['offer_ribbon', undefined] as const
  }

  return resolveRibbonKey(product)
}

export const HeroProduct = ({ product }: Props) => {
  const { openModal } = useModalContext()
  const translatable = useTranslatable()
  const t = useTranslations()
  const market = useAppSelector((state) => state.config.market)
  const currencyCountry = market?.currencyCountry
  const currency = market?.currencyCode
  const locale = useLocale()
  const ribbonData = getRibbonContent(product)
  const ribbonContent = ribbonData ? [t(ribbonData[0], ribbonData[1])] : []

  const openProduct = useCallback(() => {
    openModal({ type: 'PRODUCT', props: { product } })
    scrollModal(0, 0)
  }, [product, openModal])

  if (!product) {
    return null
  }

  return (
    <div className={styles.HeroProduct}>
      {ribbonData ? (
        <div className={styles.RibbonContainer}>
          <Ribbon content={ribbonContent} compact />
        </div>
      ) : null}
      <div className={styles.ProductContainer} onClick={openProduct}>
        <div className={styles.ProductImage}>
          <Image
            alt={translatable(product.description)}
            src={resolveProductImage(product)}
            layout="fill"
            objectFit="contain"
            priority
            sizes="(max-width: 600px) 50vw, 30vw"
            quality="100"
          />
        </div>
        <div className={styles.ProductInfo}>
          <p className={styles.Title}>{translatable(product.title)}</p>
          <ProductDescription product={product} tag="p" className={styles.Description} card />
        </div>
        <div className={styles.BuyButtonContainer}>
          <BuyButton
            additionalClasses={styles.BuyButton}
            isLocked={!market}
            product={product}
            content={currencyCountry ? formatPriceText(product.prices[currencyCountry], locale, currency) : ''}
          />
        </div>
      </div>
    </div>
  )
}
